'use client'
import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { checkEmailExists, checkUsernameExists } from '@/services/user'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Form, FormField, FormItem, FormControl, FormMessage, FormLabel, FormDescription } from "@/components/ui/form"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { User, Mail, Lock, UserCircle, Check } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { useError } from '@/contexts/ErrorContext'
import { Switch } from "@/components/ui/switch"
import { toast } from "sonner"

const formSchema = z.object({
  username: z.string()
    .min(3, "ユーザー名は3文字以上で入力してください。")
    .regex(/^[a-zA-Z0-9_-]+$/, "ユーザー名は半角英数字、ハイフン、アンダースコアのみ使用できます。"),
  email: z.string()
    .min(1, "メールアドレスを入力してください。")
    .email("有効なメールアドレスを入力してください。"),
  last_name: z.string()
    .min(1, "姓を入力してください。")
    .max(50, "姓は50文字以内で入力してください。"),
  first_name: z.string()
    .min(1, "名を入力してください。")
    .max(50, "名は50文字以内で入力してください。"),
  password: z.string()
    .min(8, "パスワードは8文字以上で入力してください。")
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
      "パスワードは大文字、小文字、数字を含む必要があります。"),
  confirmPassword: z.string()
    .min(8, "パスワードは8文字以上で入力してください。"),
  info_mail: z.boolean(),
  verificationCode: z.string().optional()
}).refine((data) => data.password === data.confirmPassword, {
  message: "パスワードが一致しません",
  path: ["confirmPassword"],
});

export default function Register() {
  useDocumentTitle('アカウント作成')
  const router = useRouter()
  const { handleError } = useError()
  const usernameCheckTimeout = useRef<NodeJS.Timeout | undefined>(undefined)
  const emailCheckTimeout = useRef<NodeJS.Timeout | undefined>(undefined)
  const [emailVerified, setEmailVerified] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isSendingCode, setIsSendingCode] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      password: "",
      confirmPassword: "",
      first_name: "",
      last_name: "",
      email: "",
      info_mail: false,
      verificationCode: ""
    },
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange"
  })

  const password = form.watch("password")
  const confirmPassword = form.watch("confirmPassword")

  useEffect(() => {
    if (password && confirmPassword && password !== confirmPassword) {
      form.setError('confirmPassword', {
        type: 'manual',
        message: 'パスワードが一致しません'
      });
    } else {
      form.clearErrors('confirmPassword');
    }
  }, [password, confirmPassword, form]);

  const username = form.watch("username")

  useEffect(() => {
    if (!username || username.length < 3) return;

    if (usernameCheckTimeout.current) {
      clearTimeout(usernameCheckTimeout.current);
    }

    usernameCheckTimeout.current = setTimeout(async () => {
      try {
        const exists = await checkUsernameExists(username);
        if (exists) {
          form.setError('username', {
            type: 'manual',
            message: 'このユーザー名は既に使用されています'
          });
        } else {
          form.clearErrors('username');
        }
      } catch (err) {
        console.error('ユーザー名重複チェックエラー:', err);
        handleError('VALIDATION_ERROR');
      }
    }, 500);

    return () => {
      if (usernameCheckTimeout.current) {
        clearTimeout(usernameCheckTimeout.current);
      }
    };
  }, [username, form, handleError]);

  const email = form.watch("email")

  useEffect(() => {
    if (!email || !form.getFieldState('email').isDirty) return;

    if (emailCheckTimeout.current) {
      clearTimeout(emailCheckTimeout.current);
    }

    emailCheckTimeout.current = setTimeout(async () => {
      try {
        const exists = await checkEmailExists(email);
        if (exists) {
          form.setError('email', {
            type: 'manual',
            message: 'このメールアドレスは既に使用されています'
          });
        } else {
          form.clearErrors('email');
        }
      } catch (err) {
        console.error('メールアドレス重複チェックエラー:', err);
        handleError('VALIDATION_ERROR');
      }
    }, 500);

    return () => {
      if (emailCheckTimeout.current) {
        clearTimeout(emailCheckTimeout.current);
      }
    };
  }, [email, form, handleError]);

  const sendVerificationCode = async () => {
    const email = form.getValues('email');
    
    if (!email || form.getFieldState('email').error) {
      toast.error("有効なメールアドレスを入力してください");
      return;
    }

    try {
      setIsSendingCode(true);
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/send-verification-code/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('確認コード送信に失敗しました');
      }

      toast.success("メールアドレスに確認コードを送信しました");
    } catch (error) {
      console.error('確認コード送信エラー:', error);
      handleError('VALIDATION_ERROR');
    } finally {
      setIsSendingCode(false);
    }
  };

  const verifyCode = async () => {
    const verificationCode = form.getValues('verificationCode');
    const email = form.getValues('email');
    
    if (!verificationCode) {
      toast.error("確認コードを入力してください");
      return;
    }

    try {
      setIsVerifying(true);
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/verify-code/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verificationCode }),
      });

      if (!response.ok) {
        throw new Error('確認コードが正しくありません');
      }

      setEmailVerified(true);
      toast.success("メールアドレスが確認されました");
    } catch (error) {
      console.error('確認コード検証エラー:', error);
      toast.error("確認コードが正しくありません");
    } finally {
      setIsVerifying(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!emailVerified) {
      toast.error("メールアドレスの確認を完了してください");
      return;
    }

    try {
      const { username, password, first_name, last_name, email, info_mail } = values;

      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/users/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          password,
          first_name,
          last_name,
          email,
          info_mail,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'アカウント作成に失敗しました');
      }

      router.push('/register/success');
    } catch (err: unknown) {
      console.error('登録エラー:', err);
      handleError('USER_CREATE_ERROR');
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-150px)] flex-col items-center justify-center p-4 sm:p-24">
      <Card className="w-full max-w-md mx-4 shadow-lg transition-shadow duration-300 hover:shadow-xl">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center">アカウント作成</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" suppressHydrationWarning>
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                        <Input placeholder="ユーザー名" className="pl-10" {...field} autoComplete="username" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                        <Input type="email" placeholder="メールアドレス" className="pl-10" {...field} autoComplete="email" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="space-y-4">
                <div className="flex space-x-2">
                  <FormField
                    control={form.control}
                    name="email"
                    render={() => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={sendVerificationCode}
                            disabled={!form.getValues('email') || !!form.getFieldState('email').error || isSendingCode || emailVerified}
                          >
                            {isSendingCode ? "送信中..." : emailVerified ? "確認済み" : "メールアドレス確認"}
                          </Button>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                {!emailVerified && (
                  <div className="flex space-x-2">
                    <FormField
                      control={form.control}
                      name="verificationCode"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <div className="relative">
                              <Check className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                              <Input placeholder="確認番号" className="pl-10" {...field} />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={verifyCode}
                      disabled={!form.getValues('verificationCode') || isVerifying}
                    >
                      {isVerifying ? "確認中..." : "確認"}
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex space-x-4">
                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <div className="relative">
                          <UserCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                          <Input placeholder="姓" className="pl-10" {...field} autoComplete="family-name" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <div className="relative">
                          <UserCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                          <Input placeholder="名" className="pl-10" {...field} autoComplete="given-name" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                        <Input type="password" placeholder="パスワード" className="pl-10" {...field} autoComplete="new-password" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                        <Input type="password" placeholder="パスワード（確認）" className="pl-10" {...field} autoComplete="new-password" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="info_mail"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                    <div className="space-y-0.5">
                      <FormLabel>お知らせメール</FormLabel>
                      <FormDescription>
                        サービスに関する重要なお知らせをメールで受け取る
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={!emailVerified}>アカウント作成</Button>
            </form>
          </Form>
          <div className="mt-4 text-center text-sm">
            <p>
              既にアカウントをお持ちの方は<Link href="/login" className="text-blue-500 hover:underline">ログイン</Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
